import React, { useEffect, useState } from 'react';
import {
    Users,
    RefreshCw,
    Key,
    User,
    Home,
    Terminal,
    Search,
    Filter,
    Shield,
    ShieldOff
} from 'lucide-react';
import { LysepAPI } from '../../../contexts/ApiContext';
import { toast } from 'react-toastify';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "../../../components/ui/Table";
import { Badge } from "../../../components/ui/Badge";
import UserSshKeysModal from '../components/UserSshKeysModal';

const UsersTab = ({ serverId }) => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [showSystemUsers, setShowSystemUsers] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [isKeysModalOpen, setIsKeysModalOpen] = useState(false);

    const fetchUsers = async () => {
        setLoading(true);
        try {
            const resp = await LysepAPI.get(`/api/servers/${serverId}/ssh/users`);
            if (resp.data.state) {
                setUsers(resp.data.data);
            } else {
                toast.error(`Kullanıcılar getirilemedi: ${resp.data.message}`);
            }
        } catch (err) {
            toast.error(`Hata: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchUsers();
    }, [serverId]);

    const filteredUsers = users.filter(user => {
        const matchesSearch = user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.comment.toLowerCase().includes(searchTerm.toLowerCase());
        const isSystemUser = user.uid < 1000 && user.username !== 'root';
        return matchesSearch && (showSystemUsers || !isSystemUser);
    });

    const openKeysModal = (user) => {
        setSelectedUser(user);
        setIsKeysModalOpen(true);
    };

    return (
        <div className="animate-fadeIn">
            <div className="bg-[#1a1a1a] border border-white/10 rounded-xl overflow-hidden shadow-lg mb-6">
                <div className="p-4 border-b border-white/10 bg-[#252525] flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div className="flex items-center gap-2">
                        <Users className="w-5 h-5 text-gray-400" />
                        <h3 className="font-semibold text-white uppercase tracking-wider text-sm">
                            Sistem Kullanıcıları
                        </h3>
                    </div>

                    <div className="flex flex-wrap items-center gap-3">
                        <div className="relative">
                            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                            <input
                                type="text"
                                placeholder="Kullanıcı ara..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="bg-[#1e1e1e] border border-white/10 rounded-lg py-1.5 pl-9 pr-3 text-xs text-white focus:outline-none focus:border-blue-500/50 w-48 transition-colors"
                            />
                        </div>

                        <button
                            onClick={() => setShowSystemUsers(!showSystemUsers)}
                            className={`flex items-center gap-2 px-3 py-1.5 text-xs font-medium rounded-lg transition-all border ${showSystemUsers
                                    ? 'bg-blue-500/10 border-blue-500/30 text-blue-400'
                                    : 'bg-[#1e1e1e] border-white/10 text-gray-400 hover:text-white'
                                }`}
                        >
                            {showSystemUsers ? <Shield className="w-3.5 h-3.5" /> : <ShieldOff className="w-3.5 h-3.5" />}
                            Sistem Kullanıcıları
                        </button>

                        <button
                            onClick={fetchUsers}
                            disabled={loading}
                            className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-gray-300 hover:text-white border border-white/10 hover:border-white/20 rounded-lg transition-colors bg-[#1e1e1e] hover:bg-[#2a2a2a] disabled:opacity-50"
                        >
                            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
                            Yenile
                        </button>
                    </div>
                </div>

                <div className="p-0 overflow-x-auto">
                    {loading ? (
                        <div className="flex flex-col items-center justify-center py-20 text-gray-400">
                            <RefreshCw className="w-8 h-8 animate-spin mb-3 text-blue-500" />
                            <p className="text-sm">Kullanıcılar yükleniyor...</p>
                        </div>
                    ) : filteredUsers.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-20 text-gray-400">
                            <Users className="w-12 h-12 opacity-10 mb-2" />
                            <p className="text-sm">Kullanıcı bulunamadı.</p>
                        </div>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Kullanıcı</TableHead>
                                    <TableHead>UID / GID</TableHead>
                                    <TableHead>Ev Dizini</TableHead>
                                    <TableHead>Kabuk (Shell)</TableHead>
                                    <TableHead className="text-right">İşlemler</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {filteredUsers.map((user) => (
                                    <TableRow key={user.username} className="group">
                                        <TableCell>
                                            <div className="flex items-center gap-3">
                                                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${user.username === 'root' ? 'bg-red-500/10 text-red-400' : 'bg-blue-500/10 text-blue-400'
                                                    }`}>
                                                    <User className="w-4 h-4" />
                                                </div>
                                                <div>
                                                    <div className="font-semibold text-white text-sm">{user.username}</div>
                                                    <div className="text-[10px] text-gray-500 truncate max-w-[150px]">{user.comment || 'Açıklama yok'}</div>
                                                </div>
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex items-center gap-1.5">
                                                <Badge variant="outline" className="text-[10px] opacity-70">U:{user.uid}</Badge>
                                                <Badge variant="outline" className="text-[10px] opacity-70">G:{user.gid}</Badge>
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex items-center gap-1.5 text-gray-400 text-xs font-mono">
                                                <Home className="w-3 h-3 text-gray-600" />
                                                {user.home}
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex items-center gap-1.5 text-gray-400 text-xs font-mono">
                                                <Terminal className="w-3 h-3 text-gray-600" />
                                                {user.shell}
                                            </div>
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <button
                                                onClick={() => openKeysModal(user)}
                                                className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-all text-xs font-medium shadow-lg shadow-blue-500/10 opacity-0 group-hover:opacity-100 focus:opacity-100"
                                            >
                                                <Key className="w-3.5 h-3.5" />
                                                SSH Anahtarları
                                            </button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </div>
            </div>

            {isKeysModalOpen && selectedUser && (
                <UserSshKeysModal
                    isOpen={isKeysModalOpen}
                    onClose={() => setIsKeysModalOpen(false)}
                    serverId={serverId}
                    user={selectedUser}
                />
            )}
        </div>
    );
};

export default UsersTab;
